// src/GDB/pages/DashboardEditPage.jsx
import { useEffect, useMemo, useState } from "react";

import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { apiGet, apiPost, apiPut, apiDelete } from "../api/client";
import ChartWidget from "../components/ChartWidget";
import TemplateGallery from "../components/TemplateGallery";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "../styles/DashboardEditPage.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

const BREAKPOINTS = {
  lg: 1200,
  md: 996,
  sm: 768,
  xs: 480,
  xxs: 0,
};

const COLS = {
  lg: 12,
  md: 12,
  sm: 12,
  xs: 12,
  xxs: 12,
};

const defaultLayoutItem = (id, idx) => ({
  i: String(id),
  x: (idx % 4) * 3, // 4 per row in 12 columns
  y: Math.floor(idx / 4) * 6,
  w: 3,
  h: 6,
});

export default function DashboardEditPage() {
  const [datasets, setDatasets] = useState([]);
  const [dataSources, setDataSources] = useState([]); // for AI
  const [dashboardName, setDashboardName] = useState("My Dashboard");
  const [widgets, setWidgets] = useState([]);
  const [selectedWidgetIdx, setSelectedWidgetIdx] = useState(null);
  const [dashboardsList, setDashboardsList] = useState([]);
  const [selectedDashboardId, setSelectedDashboardId] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // ---- Users / ownership ----
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [ownerUserId, setOwnerUserId] = useState(null);
  const [sharedUserIds, setSharedUserIds] = useState([]);

  // AI state
  const [aiGoal, setAiGoal] = useState("");
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiDatasourceId, setAiDatasourceId] = useState("");

  // Default template gallery dialog
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);

  // -------- Drilldown state --------
  const [drilldownWidget, setDrilldownWidget] = useState(null);
  const [drilldownData, setDrilldownData] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState("");
  const [drilldownFilter, setDrilldownFilter] = useState(null);
  const [drilldownSearch, setDrilldownSearch] = useState("");

  const drilldownHeaders =
    drilldownData && drilldownData.length
      ? Object.keys(drilldownData[0])
      : [];

  // Client-side drilldown filtering
  const filteredDrilldownData = useMemo(() => {
    if (!drilldownData || !drilldownData.length) return [];

    let rows = drilldownData;

    if (drilldownFilter && Object.keys(drilldownFilter).length) {
      rows = rows.filter((row) =>
        Object.entries(drilldownFilter).every(([k, v]) =>
          String(row[k] ?? "") === String(v ?? "")
        )
      );
    }

    const q = drilldownSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((row) =>
        drilldownHeaders.some((h) =>
          String(row[h] ?? "").toLowerCase().includes(q)
        )
      );
    }

    return rows;
  }, [drilldownData, drilldownFilter, drilldownHeaders, drilldownSearch]);

  // ------- Load datasets (for manual config) ------- //
  useEffect(() => {
    apiGet("/datasets/")
      .then((res) => setDatasets(res))
      .catch(console.error);
  }, []);

  // ------- Load datasources (for AI) ------- //
  useEffect(() => {
    apiGet("/datasources/")
      .then(setDataSources)
      .catch(console.error);
  }, []);

  // ------- Load users + current user (for owner & sharing) ------- //
  useEffect(() => {
    apiGet("/users/")
      .then((res) => setUsers(res || []))
      .catch(console.error);

    apiGet("/users/me")
      .then((me) => {
        setCurrentUser(me);
        setOwnerUserId((prev) => prev ?? me.id);
      })
      .catch(console.error);
  }, []);

  // ------- Load dashboards list ------- //
  const loadDashboardsList = () => {
    apiGet("/dashboards/")
      .then((res) => setDashboardsList(res))
      .catch(console.error);
  };

  useEffect(() => {
    loadDashboardsList();
  }, []);

  // -------- Check if current user is the owner -------- //
  const isOwner = useMemo(() => {
    if (!selectedDashboardId) return true; // New dashboard - user will be owner
    if (!currentUser) return false;
    return ownerUserId === currentUser.id;
  }, [selectedDashboardId, currentUser, ownerUserId]);

  // -------- Check if this is a shared dashboard (user is not owner but has access) -------- //
  const isSharedWithMe = useMemo(() => {
    if (!selectedDashboardId) return false;
    if (!currentUser) return false;
    return ownerUserId !== currentUser.id;
  }, [selectedDashboardId, currentUser, ownerUserId]);

  // Get owner display name
  const ownerDisplayName = useMemo(() => {
    if (!ownerUserId) return "Unknown";
    if (currentUser && ownerUserId === currentUser.id) {
      return `You (${currentUser.username || currentUser.email || "Current User"})`;
    }
    const owner = users.find((u) => u.id === ownerUserId);
    return owner ? (owner.username || owner.email || `User ${owner.id}`) : `User ${ownerUserId}`;
  }, [ownerUserId, currentUser, users]);

  const addWidget = (type) => {
    const id = Date.now();
    const widget = {
      id,
      title: `${type.charAt(0).toUpperCase() + type.slice(1)}`,
      widget_type: type,
      dataset_id: null,
      x_field: "",
      y_field: "",
      group_field: "",
      color: null,
      custom_colors: {},
      layout: defaultLayoutItem(id, widgets.length),
    };
    setWidgets((prev) => [...prev, widget]);
    setSelectedWidgetIdx(widgets.length);
  };

  const onLayoutChange = (currentLayout) => {
    setWidgets((prev) =>
      prev.map((w) => {
        const l = currentLayout.find((item) => item.i === String(w.id));
        return l
          ? { ...w, layout: { x: l.x, y: l.y, w: l.w, h: l.h } }
          : w;
      })
    );
  };

  const updateSelectedWidget = (patch) => {
    if (selectedWidgetIdx == null) return;
    setWidgets((prev) =>
      prev.map((w, idx) =>
        idx === selectedWidgetIdx ? { ...w, ...patch } : w
      )
    );
  };

  const removeWidgetByIndex = (idxToRemove) => {
    setWidgets((prev) => prev.filter((_, idx) => idx !== idxToRemove));
    setSelectedWidgetIdx((prevIdx) => {
      if (prevIdx == null) return null;
      if (prevIdx === idxToRemove) return null;
      if (prevIdx > idxToRemove) return prevIdx - 1;
      return prevIdx;
    });
  };

  const layout = widgets.map((w, idx) => ({
    i: String(w.id),
    ...(w.layout || defaultLayoutItem(w.id, idx)),
  }));

  const layouts = {
    lg: layout,
    md: layout,
    sm: layout,
    xs: layout,
    xxs: layout,
  };

  const selectedWidget =
    selectedWidgetIdx != null ? widgets[selectedWidgetIdx] : null;

  const handleSharedUsersChange = (e) => {
    const options = Array.from(e.target.selectedOptions || []);
    const ids = options.map((opt) => Number(opt.value));
    setSharedUserIds(ids);
  };

  // ---------------- Save / Update dashboard ---------------- //
  const saveDashboard = async () => {
    setMessage("");
    setIsLoading(true);

    if (!dashboardName.trim()) {
      setMessage("Please enter a dashboard name.");
      setIsLoading(false);
      return;
    }

    const invalid = widgets.find((w) => !w.dataset_id);
    if (invalid) {
      setMessage(`Widget "${invalid.title}" has no dataset selected.`);
      setIsLoading(false);
      return;
    }

    // For existing dashboards, only owner can save
    if (selectedDashboardId && !isOwner) {
      setMessage("❌ Only the owner can modify this dashboard.");
      setIsLoading(false);
      return;
    }

    const payload = {
      name: dashboardName.trim(),
      description: "",
      // Only include owner_user_id for new dashboards or if user is owner
      owner_user_id: selectedDashboardId
        ? ownerUserId
        : (currentUser?.id || ownerUserId),
      shared_user_ids: sharedUserIds || [],
      widgets: widgets.map((w) => {
        const base = {
          title: w.title,
          widget_type: w.widget_type,
          dataset_id: w.dataset_id,
          x_field: w.x_field,
          y_field: w.y_field,
          group_field: w.group_field,
          layout: w.layout,
        };
        if (w.color) {
          base.color = w.color;
        }
        // FIX: include custom_colors in save payload
        if (w.custom_colors && Object.keys(w.custom_colors).length > 0) {
          base.custom_colors = w.custom_colors;
        }
        return base;
      }),
    };

    try {
      let res;
      if (selectedDashboardId) {
        res = await apiPut(`/dashboards/${selectedDashboardId}`, payload);
        setMessage(`✅ Dashboard updated`);
      } else {
        res = await apiPost("/dashboards/", payload);
        setSelectedDashboardId(res.id);
        setMessage(`✅ Dashboard created`);
        if (res.owner_user_id) {
          setOwnerUserId(res.owner_user_id);
        }
        if (Array.isArray(res.shared_user_ids)) {
          setSharedUserIds(res.shared_user_ids);
        }
      }
      loadDashboardsList();
    } catch (err) {
      setMessage(`❌ Error: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- Delete dashboard ---------------- //
  const deleteDashboard = async () => {
    if (!selectedDashboardId) return;

    // Only owner can delete
    if (!isOwner) {
      setMessage("❌ Only the owner can delete this dashboard.");
      return;
    }

    const ok = window.confirm("Delete this dashboard?");
    if (!ok) return;

    setIsLoading(true);
    try {
      await apiDelete(`/dashboards/${selectedDashboardId}`);
      setMessage("🗑️ Dashboard deleted");
      setSelectedDashboardId("");
      setDashboardName("My Dashboard");
      setWidgets([]);
      setSelectedWidgetIdx(null);
      setOwnerUserId(currentUser?.id ?? null);
      setSharedUserIds([]);
      loadDashboardsList();
    } catch (err) {
      setMessage(`❌ Error: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- Load one dashboard ---------------- //
  const handleLoadDashboard = async (e) => {
    const id = e.target.value;
    setSelectedDashboardId(id);
    setMessage("");

    if (!id) {
      setDashboardName("My Dashboard");
      setWidgets([]);
      setSelectedWidgetIdx(null);
      setOwnerUserId(currentUser?.id ?? null);
      setSharedUserIds([]);
      return;
    }

    setIsLoading(true);
    try {
      const dash = await apiGet(`/dashboards/${id}`);
      setDashboardName(dash.name || "");
      // FIX: include custom_colors when loading widgets
      const loadedWidgets = (dash.widgets || []).map((w, idx) => ({
        id: w.id,
        title: w.title,
        widget_type: w.widget_type,
        dataset_id: w.dataset_id,
        x_field: w.x_field,
        y_field: w.y_field,
        group_field: w.group_field,
        color: w.color || null,
        custom_colors: w.custom_colors || {},
        layout: w.layout || defaultLayoutItem(w.id, idx),
      }));
      setWidgets(loadedWidgets);
      setSelectedWidgetIdx(loadedWidgets.length ? 0 : null);

      setOwnerUserId(dash.owner_user_id ?? null);
      setSharedUserIds(dash.shared_user_ids || []);
    } catch (err) {
      setMessage(`❌ Error: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- AI: generate dashboard by datasource + description ---------- //
  const handleGenerateWithAI = async () => {
    if (!aiDatasourceId) {
      setMessage("Select a data source for AI.");
      return;
    }
    if (!aiGoal.trim()) {
      setMessage("Please enter a description for AI.");
      return;
    }

    setIsLoading(true);
    setMessage("");
    try {
      const body = {
        datasource_id: Number(aiDatasourceId),
        goal: aiGoal,
      };

      const res = await apiPost("/ai/generate-dashboard", body);

      const ts = Date.now();
      const generatedWidgets = (res.widgets || []).map((w, idx) => {
        const id = ts + idx;
        return {
          id,
          title: w.title,
          widget_type: w.widget_type,
          dataset_id:
            typeof w.dataset_id === "number" ? w.dataset_id : null,
          x_field: w.x_field,
          y_field: w.y_field,
          group_field: w.group_field,
          color: w.color || null,
          custom_colors: w.custom_colors || {},
          layout: w.layout || defaultLayoutItem(id, idx),
        };
      });

      setDashboardName(res.name || "AI Dashboard");
      setWidgets(generatedWidgets);
      setSelectedWidgetIdx(generatedWidgets.length ? 0 : null);
      setSelectedDashboardId("");

      setOwnerUserId(currentUser?.id ?? ownerUserId);
      setSharedUserIds([]);

      setShowAiDialog(false);
      setMessage("✨ AI generated dashboard. Review and save.");
    } catch (err) {
      console.error(err);
      setMessage("❌ AI generation failed");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Template from gallery: simple apply using TemplateGallery contract ---------- //
  const applyTemplateDashboard = (template, category) => {
    if (!template) {
      setMessage("Please select a template.");
      return;
    }

    const ts = Date.now();
    const templateWidgets = (template.widgets || []).map((w, idx) => {
      const id = ts + idx + 1;
      return {
        id,
        title: w.title,
        widget_type: w.widget_type,
        dataset_id: null, // user will map datasets
        x_field: w.x_field || "",
        y_field: w.y_field || "",
        group_field: w.group_field || "",
        color: w.color || null,
        custom_colors: {},
        layout: defaultLayoutItem(id, idx),
      };
    });

    setDashboardName(
      template.dashboard_name || template.label || "New Dashboard"
    );
    setWidgets(templateWidgets);
    setSelectedWidgetIdx(templateWidgets.length ? 0 : null);
    setSelectedDashboardId("");
    setOwnerUserId(currentUser?.id ?? ownerUserId);
    setSharedUserIds([]);
    setShowTemplateDialog(false);
    setMessage(
      `🧩 "${template.label}" template applied with ${templateWidgets.length} widgets. Map datasets and save.`
    );
  };

  // ---------- Download data for ALL widgets ---------- //
  const downloadDashboardData = async () => {
    if (!widgets.length) {
      alert("No widgets to export.");
      return;
    }

    setIsLoading(true);
    try {
      const wb = XLSX.utils.book_new();
      const datasetCache = {};

      for (const w of widgets) {
        if (!w.dataset_id) continue;

        const dsId = w.dataset_id;
        if (!datasetCache[dsId]) {
          const res = await apiGet(`/query/dataset/${dsId}`);
          datasetCache[dsId] = res.data || [];
        }
        const rawRows = datasetCache[dsId];
        if (!rawRows.length) continue;

        let rowsForSheet = rawRows;
        const type = w.widget_type;

        if (type === "table") {
          rowsForSheet = rawRows;
        } else if (type === "kpi" || type === "gauge") {
          if (w.y_field) {
            rowsForSheet = rawRows.length
              ? [{ [w.y_field]: rawRows[0][w.y_field] }]
              : [];
          }
        } else {
          const fields = [
            w.x_field || null,
            w.y_field || null,
            w.group_field || null,
          ].filter(Boolean);
          if (fields.length) {
            rowsForSheet = rawRows.map((r) => {
              const obj = {};
              fields.forEach((f) => {
                obj[f] = r[f];
              });
              return obj;
            });
          } else {
            rowsForSheet = rawRows;
          }
        }

        if (!rowsForSheet.length) continue;

        const ws = XLSX.utils.json_to_sheet(rowsForSheet);
        const sheetName = (w.title || `Widget_${w.id}`)
          .replace(/[^a-zA-Z0-9]/g, "_")
          .slice(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }

      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const safeName = (dashboardName || "dashboard").replace(
        /[^a-z0-9_\-]+/gi,
        "_"
      );
      saveAs(blob, `${safeName}_data.xlsx`);
      setMessage("📊 Data exported");
    } catch (err) {
      console.error(err);
      setMessage("❌ Export failed");
    } finally {
      setIsLoading(false);
    }
  };

  const needsXY = selectedWidget
    ? [
        "bar",
        "column",
        "line",
        "area",
        "pie",
        "doughnut",
        "scatter",
        "funnel",
        "treemap",
        "waterfall",
        "radar",
        "bubble",
        "stacked_bar",
        "stacked_area",
      ].includes(selectedWidget.widget_type)
    : false;

  const isCombo = selectedWidget?.widget_type === "combo";
  const isKpi = selectedWidget?.widget_type === "kpi";
  const isGauge = selectedWidget?.widget_type === "gauge";
  const showGroupField =
    selectedWidget &&
    (
      isCombo ||
      selectedWidget.widget_type === "scatter" ||
      selectedWidget.widget_type === "bubble" ||
      selectedWidget.widget_type === "stacked_bar" ||
      selectedWidget.widget_type === "stacked_area"
    );

  const getWidgetIcon = (type) => {
    const icons = {
      bar: "📊",
      column: "📈",
      line: "📉",
      area: "🟦",
      pie: "🥧",
      doughnut: "🍩",
      combo: "🔀",
      scatter: "🔵",
      funnel: "🔄",
      treemap: "🌳",
      waterfall: "📊",
      gauge: "🎚️",
      kpi: "🔢",
      table: "📋",
      radar: "📡",
      bubble: "🫧",
      stacked_bar: "🧱",
      stacked_area: "🗺️",
    };
    return icons[type] || "📊";
  };

  // ---------- Drilldown handlers ---------- //
  const openDrilldown = async (widget, filter) => {
    if (!widget?.dataset_id) {
      setMessage("Widget has no dataset configured.");
      return;
    }

    setDrilldownData([]);
    setDrilldownError("");
    setDrilldownFilter(null);
    setDrilldownSearch("");

    setDrilldownWidget(widget);
    setDrilldownFilter(
      filter && Object.keys(filter || {}).length ? filter : null
    );
    setDrilldownLoading(true);

    try {
      const url = `/query/dataset/${widget.dataset_id}`;
      const res = await apiGet(url);
      setDrilldownData(res.data || []);
    } catch (err) {
      console.error("Drilldown error:", err);
      setDrilldownError("Failed to load data for drilldown.");
    } finally {
      setDrilldownLoading(false);
    }
  };

  const closeDrilldown = () => {
    setDrilldownWidget(null);
    setDrilldownData([]);
    setDrilldownError("");
    setDrilldownFilter(null);
    setDrilldownSearch("");
  };

  // FIX: Widget inline update handler (for color picker, etc.)
  const handleUpdateWidget = async (widgetId, patch) => {
    // 1) Update local state immediately (optimistic)
    setWidgets((prev) =>
      prev.map((w) => (w.id === widgetId ? { ...w, ...patch } : w))
    );

    // 2) Persist to backend if dashboard is already saved
    if (selectedDashboardId) {
      try {
        await apiPut(`/widgets/${widgetId}`, patch);
      } catch (err) {
        console.error("Failed to save widget update:", err);
        setMessage(`⚠️ Color saved locally but failed to persist: ${err}`);
      }
    }
  };

  return (
    <div className="dashboard-edit-page compact">
      {/* Header */}
      <div className="dashboard-header compact">
        <div className="header-content">
          <div className="header-left">
            <h1 className="header-title">Dashboard Builder</h1>
            {/* Show shared indicator */}
            {isSharedWithMe && (
              <span
                style={{
                  marginLeft: "12px",
                  padding: "4px 8px",
                  backgroundColor: "#fef3c7",
                  color: "#92400e",
                  borderRadius: "4px",
                  fontSize: "0.75rem",
                  fontWeight: "500",
                }}
              >
                👁️ View Only (Shared with you)
              </span>
            )}
          </div>

          <div className="header-actions">
            <div className="dashboard-selector">
              <select
                value={selectedDashboardId}
                onChange={handleLoadDashboard}
                className="selector-input"
                disabled={isLoading}
              >
                <option value="">+ New Dashboard</option>
                {dashboardsList.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Default templates button - only for new or owned dashboards */}
            {!isSharedWithMe && (
              <button
                type="button"
                className="btn btn--secondary btn--xs"
                disabled={isLoading}
                onClick={() => setShowTemplateDialog(true)}
                title="Add default dashboard template"
              >
                📚
              </button>
            )}

            {/* AI generate button - only for new or owned dashboards */}
            {!isSharedWithMe && (
              <button
                type="button"
                className="btn btn--secondary btn--xs"
                disabled={isLoading || !dataSources.length}
                onClick={() => setShowAiDialog(true)}
                title="Generate with AI"
              >
                ✨
              </button>
            )}

            <button
              onClick={downloadDashboardData}
              className="btn btn--secondary btn--xs"
              disabled={isLoading || !widgets.length}
              title="Export Data"
            >
              📥
            </button>

            {/* Delete button - only for owner */}
            {selectedDashboardId && isOwner && (
              <button
                onClick={deleteDashboard}
                className="btn btn--danger btn--xs"
                disabled={isLoading}
                title="Delete Dashboard"
              >
                🗑️
              </button>
            )}

            {/* Save/Update button - only for owner */}
            {!isSharedWithMe && (
              <button
                onClick={saveDashboard}
                className="btn btn--primary btn--xs"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="button-spinner"></div>
                ) : selectedDashboardId ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main compact">
        {/* Sidebar */}
        <div className="dashboard-sidebar compact">
          {/* Dashboard Settings */}
          <div className="sidebar-card compact">
            <div className="form-group compact">
              <label className="form-label">Dashboard Name</label>
              <input
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                className="form-input compact"
                placeholder="Dashboard name"
                disabled={isSharedWithMe}
              />
            </div>

            {/* Owner - Read-only display for everyone, no dropdown to change */}
            <div className="form-group compact">
              <label className="form-label">Owner</label>
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "6px",
                  fontSize: "0.875rem",
                  color: "#374151",
                  border: "1px solid #e5e7eb",
                }}
              >
                {ownerDisplayName}
              </div>
              {!selectedDashboardId && (
                <small className="form-help" style={{ color: "#6b7280" }}>
                  You will be the owner of this new dashboard.
                </small>
              )}
            </div>

            {/* Shared users - Only visible to owner */}
            {isOwner && (
              <div className="form-group compact">
                <label className="form-label">Share with users</label>
                <select
                  multiple
                  value={sharedUserIds.map((id) => String(id))}
                  onChange={handleSharedUsersChange}
                  className="form-select compact"
                  style={{ minHeight: "90px" }}
                >
                  {users
                    .filter((u) => u.id !== currentUser?.id) // Don't show current user in share list
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username || u.email || `User ${u.id}`}
                      </option>
                    ))}
                </select>
                <small className="form-help">
                  Hold Ctrl / Cmd to select multiple users.
                </small>
              </div>
            )}

            {/* For shared dashboards, show who it's shared with (read-only) */}
            {isSharedWithMe && sharedUserIds.length > 0 && (
              <div className="form-group compact">
                <label className="form-label">Also shared with</label>
                <div
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#f3f4f6",
                    borderRadius: "6px",
                    fontSize: "0.875rem",
                    color: "#6b7280",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {sharedUserIds
                    .filter((id) => id !== currentUser?.id)
                    .map((id) => {
                      const user = users.find((u) => u.id === id);
                      return user
                        ? user.username || user.email || `User ${id}`
                        : `User ${id}`;
                    })
                    .join(", ") || "No other users"}
                </div>
              </div>
            )}
          </div>

          {/* Widget Palette - Only for owner */}
          {!isSharedWithMe && (
            <div className="sidebar-card compact">
              <h3 className="card-title compact">Widgets</h3>

              <div className="widget-category">
                <h4 className="category-title compact">Charts</h4>
                <div className="widget-grid compact">
                  {["bar", "column", "line", "area", "pie", "doughnut", "radar"].map(
                    (type) => (
                      <button
                        key={type}
                        onClick={() => addWidget(type)}
                        className="widget-button compact"
                        title={type}
                        style={{
                          transform: "perspective(500px) translateZ(0)",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform =
                            "perspective(500px) translateZ(10px)";
                          e.currentTarget.style.boxShadow =
                            "0 8px 20px rgba(0,0,0,0.15)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform =
                            "perspective(500px) translateZ(0)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 8px rgba(0,0,0,0.1)";
                        }}
                      >
                        <span className="widget-icon">
                          {getWidgetIcon(type)}
                        </span>
                      </button>
                    )
                  )}
                </div>
              </div>

              <div className="widget-category">
                <h4 className="category-title compact">Advanced</h4>
                <div className="widget-grid compact">
                  {[
                    "combo",
                    "scatter",
                    "bubble",
                    "stacked_bar",
                    "stacked_area",
                    "funnel",
                    "treemap",
                    "waterfall",
                    "gauge",
                  ].map((type) => (
                    <button
                      key={type}
                      onClick={() => addWidget(type)}
                      className="widget-button compact"
                      title={type}
                      style={{
                        transform: "perspective(500px) translateZ(0)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform =
                          "perspective(500px) translateZ(10px)";
                        e.currentTarget.style.boxShadow =
                          "0 8px 20px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform =
                          "perspective(500px) translateZ(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.1)";
                      }}
                    >
                      <span className="widget-icon">
                        {getWidgetIcon(type)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="widget-category">
                <h4 className="category-title compact">Data</h4>
                <div className="widget-grid compact">
                  {["kpi", "table"].map((type) => (
                    <button
                      key={type}
                      onClick={() => addWidget(type)}
                      className="widget-button compact"
                      title={type}
                      style={{
                        transform: "perspective(500px) translateZ(0)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform =
                          "perspective(500px) translateZ(10px)";
                        e.currentTarget.style.boxShadow =
                          "0 8px 20px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform =
                          "perspective(500px) translateZ(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.1)";
                      }}
                    >
                      <span className="widget-icon">
                        {getWidgetIcon(type)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Widget Configuration - Only for owner */}
          {!isSharedWithMe && (
            <div className="sidebar-card compact config-card">
              <h3 className="card-title compact">Configuration</h3>
              {selectedWidget ? (
                <div className="config-form compact">
                  <div className="widget-header">
                    <span className="widget-type-badge compact">
                      {getWidgetIcon(selectedWidget.widget_type)}{" "}
                      {selectedWidget.widget_type}
                    </span>
                  </div>

                  <div className="form-group compact">
                    <label className="form-label">Title</label>
                    <input
                      value={selectedWidget.title}
                      onChange={(e) =>
                        updateSelectedWidget({ title: e.target.value })
                      }
                      className="form-input compact"
                    />
                  </div>

                  <div className="form-group compact">
                    <label className="form-label">Dataset</label>
                    <select
                      value={selectedWidget.dataset_id || ""}
                      onChange={(e) =>
                        updateSelectedWidget({
                          dataset_id: e.target.value
                            ? Number(e.target.value)
                            : null,
                        })
                      }
                      className="form-select compact"
                    >
                      <option value="">Select dataset</option>
                      {datasets.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Color picker */}
                  <div className="form-group compact">
                    <label className="form-label">Color</label>
                    <input
                      type="color"
                      value={selectedWidget.color || "#2563EB"}
                      onChange={(e) =>
                        updateSelectedWidget({
                          color: e.target.value || null,
                        })
                      }
                      className="form-input compact"
                      style={{
                        padding: 0,
                        height: "32px",
                        cursor: "pointer",
                      }}
                    />
                  </div>

                  {needsXY && (
                    <>
                      <div className="form-group compact">
                        <label className="form-label">X Field</label>
                        <input
                          value={selectedWidget.x_field || ""}
                          onChange={(e) =>
                            updateSelectedWidget({
                              x_field: e.target.value,
                            })
                          }
                          className="form-input compact"
                        />
                      </div>

                      <div className="form-group compact">
                        <label className="form-label">Y Field</label>
                        <input
                          value={selectedWidget.y_field || ""}
                          onChange={(e) =>
                            updateSelectedWidget({
                              y_field: e.target.value,
                            })
                          }
                          className="form-input compact"
                        />
                      </div>
                    </>
                  )}

                  {isCombo && (
                    <>
                      <div className="form-group compact">
                        <label className="form-label">X Field</label>
                        <input
                          value={selectedWidget.x_field || ""}
                          onChange={(e) =>
                            updateSelectedWidget({
                              x_field: e.target.value,
                            })
                          }
                          className="form-input compact"
                        />
                      </div>

                      <div className="form-group compact">
                        <label className="form-label">Bar Field</label>
                        <input
                          value={selectedWidget.y_field || ""}
                          onChange={(e) =>
                            updateSelectedWidget({
                              y_field: e.target.value,
                            })
                          }
                          className="form-input compact"
                        />
                      </div>

                      <div className="form-group compact">
                        <label className="form-label">Line Field</label>
                        <input
                          value={selectedWidget.group_field || ""}
                          onChange={(e) =>
                            updateSelectedWidget({
                              group_field: e.target.value,
                            })
                          }
                          className="form-input compact"
                        />
                      </div>
                    </>
                  )}

                  {showGroupField && !isCombo && (
                    <div className="form-group compact">
                      <label className="form-label">Group / Size Field</label>
                      <input
                        value={selectedWidget.group_field || ""}
                        onChange={(e) =>
                          updateSelectedWidget({
                            group_field: e.target.value,
                          })
                        }
                        className="form-input compact"
                      />
                    </div>
                  )}

                  {(isKpi || isGauge) && (
                    <div className="form-group compact">
                      <label className="form-label">Value Field</label>
                      <input
                        value={selectedWidget.y_field || ""}
                        onChange={(e) =>
                          updateSelectedWidget({
                            y_field: e.target.value,
                          })
                        }
                        className="form-input compact"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="config-empty compact">
                  <div className="empty-icon">👆</div>
                  <p>Select a widget</p>
                </div>
              )}
            </div>
          )}

          {/* For shared dashboards, show a simplified info panel */}
          {isSharedWithMe && (
            <div className="sidebar-card compact">
              <h3 className="card-title compact">Dashboard Info</h3>
              <div
                style={{
                  padding: "12px 0",
                  color: "#6b7280",
                  fontSize: "0.875rem",
                }}
              >
                <p style={{ marginBottom: "8px" }}>
                  This dashboard has been shared with you by{" "}
                  <strong>{ownerDisplayName}</strong>.
                </p>
                <p style={{ marginBottom: "8px" }}>
                  You can view the data and export it, but you cannot modify the
                  dashboard configuration.
                </p>
                <p>
                  <strong>{widgets.length}</strong> widget
                  {widgets.length !== 1 ? "s" : ""} configured.
                </p>
              </div>
            </div>
          )}

          {/* Messages */}
          {message && (
            <div
              className={`message compact ${
                message.includes("❌")
                  ? "message--error"
                  : "message--success"
              }`}
            >
              {message}
            </div>
          )}
        </div>

        {/* Canvas */}
        <div className="dashboard-canvas compact">
          {widgets.length === 0 ? (
            <div className="canvas-empty compact">
              <div className="empty-illustration">📊</div>
              <p>
                {isSharedWithMe
                  ? "This dashboard has no widgets"
                  : "Add widgets to start"}
              </p>
            </div>
          ) : (
            <ResponsiveGridLayout
              className="dashboard-grid compact"
              layouts={layouts}
              breakpoints={BREAKPOINTS}
              cols={COLS}
              rowHeight={30}
              onLayoutChange={isSharedWithMe ? undefined : onLayoutChange}
              draggableCancel=".widget-remove-btn"
              isResizable={!isSharedWithMe}
              isDraggable={!isSharedWithMe}
              margin={[8, 8]}
              style={{
                transform: "perspective(1000px)",
              }}
            >
              {widgets.map((w, idx) => (
                <div
                  key={w.id}
                  data-grid={layout.find((l) => l.i === String(w.id))}
                  onClick={() => !isSharedWithMe && setSelectedWidgetIdx(idx)}
                  className={`grid-item compact ${
                    selectedWidgetIdx === idx && !isSharedWithMe
                      ? "grid-item--selected"
                      : ""
                  }`}
                  style={{
                    transform: "perspective(1000px)",
                    transition: "all 0.3s ease",
                    cursor: isSharedWithMe ? "default" : "pointer",
                  }}
                >
                  {/* FIX: pass onUpdateWidget so color picker can save */}
                  <ChartWidget
                    widget={w}
                    onRemove={
                      isSharedWithMe ? undefined : () => removeWidgetByIndex(idx)
                    }
                    onDrilldown={(filter) => openDrilldown(w, filter)}
                    onUpdateWidget={isSharedWithMe ? undefined : handleUpdateWidget}
                    compact={true}
                  />
                </div>
              ))}
            </ResponsiveGridLayout>
          )}
        </div>
      </div>

      {/* AI Dialog */}
      {showAiDialog && (
        <div className="ai-dialog-backdrop compact">
          <div className="ai-dialog">
            <div className="ai-dialog-title">Generate Dashboard with AI</div>

            <div className="form-group compact">
              <label className="form-label">Data Source</label>
              <select
                value={aiDatasourceId}
                onChange={(e) => setAiDatasourceId(e.target.value)}
                className="form-select"
              >
                <option value="">Select data source</option>
                {dataSources.map((ds) => (
                  <option key={ds.id} value={ds.id}>
                    {ds.name} ({ds.db_type})
                  </option>
                ))}
              </select>
            </div>

            <textarea
              className="ai-textarea"
              rows={4}
              placeholder="Describe the dashboard you want (e.g., Sales trend by month, top 10 customers, region-wise performance)"
              value={aiGoal}
              onChange={(e) => setAiGoal(e.target.value)}
            />
            <div className="ai-dialog-actions">
              <button
                type="button"
                className="btn btn--secondary btn--xs"
                onClick={() => setShowAiDialog(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary btn--xs"
                onClick={handleGenerateWithAI}
                disabled={isLoading || !dataSources.length}
              >
                {isLoading ? "Generating..." : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Default Template Dialog (uses TemplateGallery) */}
      {showTemplateDialog && (
        <TemplateGallery
          onSelectTemplate={applyTemplateDashboard}
          onClose={() => setShowTemplateDialog(false)}
        />
      )}

      {/* Drilldown panel (popup) */}
      {drilldownWidget && (
        <div
          className="drilldown-backdrop"
          onClick={closeDrilldown}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="drilldown-panel"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "90%",
              height: "80%",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              transform: "perspective(1000px) translateZ(50px)",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            }}
          >
            <div
              className="drilldown-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "20px",
                borderBottom: "1px solid #e5e7eb",
                paddingBottom: "10px",
              }}
            >
              <div>
                <h3
                  className="drilldown-title"
                  style={{ margin: 0, fontSize: "1.5rem", fontWeight: "600" }}
                >
                  Drilldown – {drilldownWidget.title}
                </h3>
                <p
                  className="drilldown-subtitle"
                  style={{
                    margin: "5px 0 0 0",
                    color: "#6b7280",
                    fontSize: "0.9rem",
                  }}
                >
                  Dataset ID: {drilldownWidget.dataset_id}
                </p>
                {drilldownFilter && (
                  <p
                    className="drilldown-filter"
                    style={{
                      margin: "5px 0 0 0",
                      color: "#374151",
                      fontSize: "0.9rem",
                    }}
                  >
                    Filters:&nbsp;
                    {Object.entries(drilldownFilter)
                      .map(([k, v]) => `${k} = ${v}`)
                      .join(", ")}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="btn btn--icon"
                onClick={closeDrilldown}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  padding: "5px",
                  borderRadius: "4px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "rgba(0,0,0,0.1)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "transparent")
                }
              >
                ✕
              </button>
            </div>

            {drilldownLoading ? (
              <div
                className="drilldown-loading"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                }}
              >
                <div
                  className="loading-spinner"
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #f3f4f6",
                    borderTop: "4px solid #3b82f6",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
              </div>
            ) : drilldownError ? (
              <div
                className="message message--error compact"
                style={{
                  padding: "16px",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  color: "#dc2626",
                  margin: "20px 0",
                }}
              >
                {drilldownError}
              </div>
            ) : !drilldownData.length ? (
              <div
                className="drilldown-empty"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  color: "#6b7280",
                }}
              >
                <div
                  className="empty-icon"
                  style={{ fontSize: "3rem", marginBottom: "1rem" }}
                >
                  👀
                </div>
                <p>No data returned for this dataset.</p>
              </div>
            ) : (
              <>
                {/* Search + row count */}
                <div
                  className="drilldown-search-row"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "#6b7280",
                    }}
                  >
                    Showing {filteredDrilldownData.length} of{" "}
                    {drilldownData.length} rows
                    {drilldownSearch ? " (filtered)" : ""}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search in results..."
                      value={drilldownSearch}
                      onChange={(e) =>
                        setDrilldownSearch(e.target.value)
                      }
                      style={{
                        padding: "6px 10px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        fontSize: "0.85rem",
                        minWidth: "220px",
                        outline: "none",
                      }}
                    />
                    {drilldownSearch && (
                      <button
                        type="button"
                        onClick={() => setDrilldownSearch("")}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "1px solid #d1d5db",
                          backgroundColor: "#f9fafb",
                          fontSize: "0.8rem",
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                <div
                  className="drilldown-table-wrapper"
                  style={{
                    flex: 1,
                    overflow: "auto",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                >
                  {filteredDrilldownData.length === 0 ? (
                    <div
                      style={{
                        padding: "16px",
                        fontSize: "0.9rem",
                        color: "#6b7280",
                        textAlign: "center",
                      }}
                    >
                      No rows match your search.
                    </div>
                  ) : (
                    <table
                      className="drilldown-table"
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: "#f9fafb" }}>
                          {drilldownHeaders.map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: "12px",
                                textAlign: "left",
                                borderBottom: "1px solid #e5e7eb",
                                fontWeight: "600",
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#f9fafb",
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDrilldownData.map((row, i) => (
                          <tr
                            key={i}
                            style={{
                              backgroundColor:
                                i % 2 === 0 ? "#ffffff" : "#f9fafb",
                            }}
                          >
                            {drilldownHeaders.map((h) => (
                              <td
                                key={h}
                                style={{
                                  padding: "12px",
                                  borderBottom:
                                    "1px solid #e5e7eb",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {String(row[h] ?? "")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay compact">
          <div className="loading-content">
            <div className="loading-spinner"></div>
          </div>
        </div>
      )}
    </div>
  );
}